import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, TextField, Grid, Typography, Autocomplete,
  FormControl, FormGroup, FormControlLabel, Checkbox, Stack, Alert, Tooltip,
} from '@mui/material';
import Inventory2 from '@mui/icons-material/Inventory2';
import CategoryIcon from '@mui/icons-material/Category';
import Storefront from '@mui/icons-material/Storefront';
import Rule from '@mui/icons-material/Rule';
import LocalShipping from '@mui/icons-material/LocalShipping';
import Sync from '@mui/icons-material/Sync';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import {
  client, MarketCatalogue, ReferenceInstance, ScopedReferenceMarket, MarketOrderType, MarketSupplier, MarketStore, MarketItem,
} from '../../../app/services/api/orderManagementClient';
import { isOrderTypeDSD } from '../../../enums/OrderType';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { Section } from '../../../components/ui/Section';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';
import { DetailPageTemplate } from '../../../components/page-templates/detail-page/DetailPageTemplate';
import type { TabDefinition } from '../../../components/shared/tabs/TabbedSection';
import { InsightTile } from '../../../components/shared/tiles/InsightTile';
import { TableSection, TableColumn } from '../../../components/shared/tables/TableSection';
import { applyRoundAndFormat } from '../../../utils';

const CatalogueForm = () => {
  const { t } = useTranslation('catalogues');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const isEdit = Boolean(id);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { instances, getMarkets, ensureMarketsLoaded } = useInstanceMarket();
  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [suppliers, setSuppliers] = useState<MarketSupplier[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  
  const [formData, setFormData] = useState<MarketCatalogue>({
    customerGroupId: 0,
    instanceName: '',
    companyId: 0,
    name: '',
    message: '',
    marketName: '',
    customerTypeId: 0,
    erpInternalId: undefined,
    orderTypeIds: [],
    supplierIds: [],
  });

  const [selectedInstance, setSelectedInstance] = useState<ReferenceInstance | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<ScopedReferenceMarket | null>(null);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState<MarketOrderType[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<MarketSupplier[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Headline tiles/tabs are read-only summary data layered onto the existing form - they never affect save/update behaviour.
  const canViewCatalogueStores = hasPermissionTo(['StoreView', 'ConfigurationAdmin']);
  const canViewCatalogueProducts = hasPermissionTo(['ProductView', 'ConfigurationAdmin']);
  const canEditProducts = hasPermissionTo(['ProductUpdate', 'ConfigurationAdmin']);
  const canEditStores = hasPermissionTo(['StoreUpdate', 'ConfigurationAdmin']);

  const [marketStores, setMarketStores] = useState<MarketStore[]>([]);
  const [assignedStoresTotalCount, setAssignedStoresTotalCount] = useState<number>(0);
  const [isStoresLoading, setIsStoresLoading] = useState<boolean>(false);
  const [marketItems, setMarketItems] = useState<MarketItem[]>([]);
  const [isItemsLoading, setIsItemsLoading] = useState<boolean>(false);
  const [productsPage, setProductsPage] = useState<number>(0);
  const [productsSearchInput, setProductsSearchInput] = useState<string>('');
  const [storesPage, setStoresPage] = useState<number>(0);
  const [storesSearchInput, setStoresSearchInput] = useState<string>('');

  const markets = getMarkets(selectedInstance ? selectedInstance.id.toString() : '');

  const assignedStores = canViewCatalogueStores ? marketStores : [];

  // Products/categories for a catalogue are a market-level relationship (MarketItem.catalogueIds), not store-specific.
  const isItemActiveInCatalogue = (item: MarketItem): boolean => {
    const entry = item.catalogueEntries?.find((e) => e.catalogueId === formData.customerGroupId);
    if (!entry) return true;
    const today = new Date().toISOString().slice(0, 10);
    if (entry.startDate && entry.startDate.slice(0, 10) > today) return false;
    if (entry.endDate && entry.endDate.slice(0, 10) < today) return false;
    return true;
  };

  const catalogueItems = useMemo(() => (
    canViewCatalogueProducts
      ? marketItems.filter((item) => (item.catalogueIds ?? []).includes(formData.customerGroupId))
      : []
  ), [canViewCatalogueProducts, marketItems, formData.customerGroupId]);

  const activeCatalogueItemsCount = useMemo(() => (
    catalogueItems.filter(isItemActiveInCatalogue).length
  ), [catalogueItems, formData.customerGroupId]);

  const catalogueCategoriesCount = useMemo(() => {
    const categoryNames = catalogueItems
      .map((item) => item.marketPrimaryCategoryName || item.primaryCategoryName)
      .filter((name): name is string => Boolean(name));
    return new Set(categoryNames).size;
  }, [catalogueItems]);

  const filteredCatalogueItems = useMemo(() => {
    const search = productsSearchInput.trim().toLowerCase();
    if (!search) return catalogueItems;
    return catalogueItems.filter((item) => (
      item.itemName?.toLowerCase().includes(search)
      || item.marketItemName?.toLowerCase().includes(search)
      || item.itemNumber?.toLowerCase().includes(search)
    ));
  }, [catalogueItems, productsSearchInput]);

  const productsPageSize = 10;
  const productsTotalPages = Math.max(1, Math.ceil(filteredCatalogueItems.length / productsPageSize));
  const pagedCatalogueItems = filteredCatalogueItems.slice(
    productsPage * productsPageSize,
    (productsPage + 1) * productsPageSize,
  );

  const storesPageSize = 10;
  const storesTotalPages = Math.max(1, Math.ceil(assignedStoresTotalCount / storesPageSize));

  const fetchOrderTypes = async (companyId: number, customerTypeId?: number) => {
    try {
      const result = await client.getMarketOrderTypes({
        companyId: companyId.toString(),
        customerTypeId: customerTypeId?.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setOrderTypes(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching order types:', error);
    }
  };

  const fetchSuppliers = async (companyId: number, customerTypeId?: number) => {
    try {
      const result = await client.getMarketSuppliers({
        companyId: companyId.toString(),
        customerTypeId: customerTypeId?.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setSuppliers(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchCatalogue = async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const result = await client.getMarketCatalogue(id);
      if (result.success && result.data) {
        const catalogue = result.data;
        setFormData({
          ...catalogue,
          orderTypeIds: catalogue.orderTypeIds ?? [],
          supplierIds: catalogue.supplierIds ?? [],
        });
        const instance = instances.find(i => i.id === catalogue.companyId);
        if (instance) {
          setSelectedInstance(instance);
          ensureMarketsLoaded(instance.id.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching catalogue:', error);
      setMessage(t('management.loadErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (instances.length > 0 && isEdit && id) {
      fetchCatalogue();
    }
  }, [instances, isEdit, id]);

  useEffect(() => {
    if (isEdit && formData.customerGroupId && selectedInstance && markets.length > 0) {
      const market = markets.find(m => m.customerTypeId === formData.customerTypeId);
      if (market && !selectedMarket) {
        setSelectedMarket(market);
        fetchOrderTypes(selectedInstance.id, market.customerTypeId);
        fetchSuppliers(selectedInstance.id, market.customerTypeId);
      }
    }
  }, [markets, formData.customerTypeId, selectedInstance, isEdit]);

  const loadMarketStores = async (instanceId: string, marketId: string) => {
    setIsStoresLoading(true);
    try {
      // Server-side filtered by catalogueId - only this catalogue's stores are ever fetched, with real pagination/search.
      const result = await client.getMarketStores({
        companyId: instanceId,
        customerTypeId: marketId,
        catalogueId: formData.customerGroupId.toString(),
        searchTerm: storesSearchInput,
        sort: 'storenumber',
        direction: 'asc',
        pageNumber: storesPage,
        pageSize: storesPageSize,
      });
      if (result.success && result.data) {
        setMarketStores(result.data.items ?? []);
        setAssignedStoresTotalCount(result.data.totalCount ?? 0);
      }
    } catch (error) {
      console.error('Error fetching market stores:', error);
    } finally {
      setIsStoresLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit && canViewCatalogueStores && selectedInstance && selectedMarket && formData.customerGroupId) {
      loadMarketStores(selectedInstance.id.toString(), selectedMarket.customerTypeId.toString());
    }
  }, [isEdit, canViewCatalogueStores, selectedInstance, selectedMarket, formData.customerGroupId, storesPage, storesSearchInput]);

  useEffect(() => {
    setStoresPage(0);
  }, [storesSearchInput]);

  const loadMarketItems = async (instanceId: string, marketId: string) => {
    setIsItemsLoading(true);
    try {
      const result = await client.getMarketItems({
        companyId: instanceId,
        customerTypeId: marketId,
        searchTerm: '',
        sort: '',
        direction: '',
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setMarketItems(result.data.items ?? []);
      }
    } catch (error) {
      console.error('Error fetching market items:', error);
    } finally {
      setIsItemsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit && canViewCatalogueProducts && selectedInstance && selectedMarket) {
      loadMarketItems(selectedInstance.id.toString(), selectedMarket.customerTypeId.toString());
    }
  }, [isEdit, canViewCatalogueProducts, selectedInstance, selectedMarket]);

  useEffect(() => {
    setProductsPage(0);
  }, [productsSearchInput]);

  useEffect(() => {
    const orderTypeIds = formData.orderTypeIds ?? [];
    if (isEdit && orderTypeIds.length > 0 && orderTypes.length > 0) {
      const orderTypesData = orderTypes.filter(ot => orderTypeIds.includes(ot.orderTypeId));
      if (orderTypesData.length > 0 && selectedOrderTypes.length === 0) {
        setSelectedOrderTypes(orderTypesData);
      }
    }
  }, [orderTypes, formData.orderTypeIds, isEdit]);

  useEffect(() => {
    const supplierIds = formData.supplierIds ?? [];
    if (suppliers.length > 0 && selectedSuppliers.length === 0) {
      if (isEdit && supplierIds.length > 0) {
        const suppliersData = suppliers.filter(s => supplierIds.includes(s.supplierId));
        if (suppliersData.length > 0) {
          setSelectedSuppliers(suppliersData);
        }
      } else if (formData.name && selectedOrderTypes.some(ot => isOrderTypeDSD(ot.orderTypeId))) {
        const matchingSuppliers = suppliers.filter(supplier => 
          supplier.supplierName === formData.name,
        );
        
        if (matchingSuppliers.length > 0) {
          setSelectedSuppliers(matchingSuppliers);
          setFormData(prev => ({
            ...prev,
            supplierIds: matchingSuppliers.map(s => s.supplierId),
          }));
        }
      }
    }
  }, [suppliers, formData.supplierIds, formData.name, selectedOrderTypes, selectedSuppliers.length, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('management.catalogueNameRequired');
    }

    if (!selectedInstance) {
      newErrors.instance = t('management.instanceRequired');
    }

    if (!selectedMarket) {
      newErrors.market = t('management.marketRequired');
    }

    if (selectedOrderTypes.length === 0) {
      newErrors.orderTypes = t('management.orderTypesRequired');
    }

    const isDsdSelected = selectedOrderTypes.some(ot => isOrderTypeDSD(ot.orderTypeId));
    if (isDsdSelected && selectedSuppliers.length === 0) {
      newErrors.suppliers = t('management.suppliersRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketCatalogue) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: newValue,
    }));
    
    if (field === 'name' && selectedOrderTypes.some(ot => isOrderTypeDSD(ot.orderTypeId)) && newValue) {
      const matchingSuppliers = suppliers.filter(supplier => 
        supplier.supplierName === newValue,
      );
      
      if (matchingSuppliers.length > 0) {
        const newSelectedSuppliers = [...selectedSuppliers];
        matchingSuppliers.forEach(supplier => {
          if (!newSelectedSuppliers.some(s => s.supplierId === supplier.supplierId)) {
            newSelectedSuppliers.push(supplier);
          }
        });
        
        setSelectedSuppliers(newSelectedSuppliers);
        setFormData(prev => ({
          ...prev,
          supplierIds: newSelectedSuppliers.map(s => s.supplierId),
        }));
      }
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: ReferenceInstance | null) => {
    setSelectedInstance(value);
    setSelectedMarket(null);
    setSelectedOrderTypes([]);
    setSelectedSuppliers([]);
    setOrderTypes([]);
    setSuppliers([]);
    
    if (value) {
      setFormData(prev => ({
        ...prev,
        companyId: value.id,
        instanceName: value.name,
      }));
      ensureMarketsLoaded(value.id.toString());
    } else {
      setFormData(prev => ({
        ...prev,
        companyId: 0,
        instanceName: '',
      }));
    }
    
    if (errors.instance) {
      setErrors(prev => ({ ...prev, instance: '' }));
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: ScopedReferenceMarket | null) => {
    setSelectedMarket(value);
    setSelectedOrderTypes([]);
    setSelectedSuppliers([]);
    setOrderTypes([]);
    setSuppliers([]);
    
    if (value) {
      setFormData(prev => ({
        ...prev,
        customerTypeId: value.customerTypeId,
        marketName: value.name,
      }));
      if (selectedInstance) {
        fetchOrderTypes(selectedInstance.id, value.customerTypeId);
        fetchSuppliers(selectedInstance.id, value.customerTypeId);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        customerTypeId: 0,
        marketName: '',
      }));
    }
    
    if (errors.market) {
      setErrors(prev => ({ ...prev, market: '' }));
    }
  };

  const handleOrderTypeChange = (orderType: MarketOrderType, checked: boolean) => {
    let newSelectedOrderTypes: MarketOrderType[];
    
    if (checked) {
      newSelectedOrderTypes = [...selectedOrderTypes, orderType];
      
      if (isOrderTypeDSD(orderType.orderTypeId) && formData.name) {
        const matchingSuppliers = suppliers.filter(supplier => 
          supplier.supplierName === formData.name,
        );
        
        const newSelectedSuppliers = [...selectedSuppliers];
        matchingSuppliers.forEach(supplier => {
          if (!newSelectedSuppliers.some(s => s.supplierId === supplier.supplierId)) {
            newSelectedSuppliers.push(supplier);
          }
        });
        
        setSelectedSuppliers(newSelectedSuppliers);
        setFormData(prev => ({
          ...prev,
          supplierIds: newSelectedSuppliers.map(s => s.supplierId),
        }));
      }
    } else {
      newSelectedOrderTypes = selectedOrderTypes.filter(ot => ot.orderTypeId !== orderType.orderTypeId);
      
      if (isOrderTypeDSD(orderType.orderTypeId)) {
        setSelectedSuppliers([]);
        setFormData(prev => ({
          ...prev,
          supplierIds: [],
        }));
      }
    }
    
    setSelectedOrderTypes(newSelectedOrderTypes);
    setFormData(prev => ({
      ...prev,
      orderTypeIds: newSelectedOrderTypes.map(ot => ot.orderTypeId),
      message: newSelectedOrderTypes.length === 1 && isOrderTypeDSD(newSelectedOrderTypes[0].orderTypeId) ? '12' : '',
    }));
    
    if (errors.orderTypes) {
      setErrors(prev => ({ ...prev, orderTypes: '' }));
    }
  };

  const handleSupplierChange = (supplier: MarketSupplier, checked: boolean) => {
    let newSelectedSuppliers: MarketSupplier[];
    
    if (checked) {
      newSelectedSuppliers = [...selectedSuppliers, supplier];
    } else {
      newSelectedSuppliers = selectedSuppliers.filter(s => s.supplierId !== supplier.supplierId);
    }
    
    setSelectedSuppliers(newSelectedSuppliers);
    setFormData(prev => ({
      ...prev,
      supplierIds: newSelectedSuppliers.map(s => s.supplierId),
    }));
    
    if (errors.suppliers) {
      setErrors(prev => ({ ...prev, suppliers: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      let result;
      
      if (isEdit) {
        result = await client.updateMarketCatalogue(id!, formData);
        if (result.success) {
          setMessage(t('management.updateSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          const params = getUrlParams();
          navigate(`/procurement/catalogues${params.toString() ? `?${params.toString()}` : ''}`);
        } else {
          setMessage(t('management.updateErrorMessage'));
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        result = await client.createMarketCatalogue(formData);
        if (result.success) {
          setMessage(t('management.createSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          const params = getUrlParams();
          navigate(`/procurement/catalogues${params.toString() ? `?${params.toString()}` : ''}`);
        } else {
          setMessage(t('management.createErrorMessage'));
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      console.error('Error saving catalogue:', error);
      setMessage(isEdit ? t('management.updateErrorMessage') : t('management.createErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/procurement/catalogues${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'CatalogueUpdate' : 'CatalogueCreate', 'ConfigurationAdmin']);
  const isDsdSelected = selectedOrderTypes.some(ot => isOrderTypeDSD(ot.orderTypeId));

  const truncateText = (text: string, maxLength: number = 40) => {
    if (!text) return '';
    return text.length <= maxLength ? text : `${text.substring(0, maxLength)}...`;
  };

  const handleViewProduct = (product: MarketItem) => {
    if (!canEditProducts) return;
    const params = new URLSearchParams();
    params.set('companyId', formData.companyId.toString());
    if (formData.customerTypeId) params.set('customerTypeId', formData.customerTypeId.toString());
    navigate(`/procurement/products/edit/${product.contentId}?${params.toString()}`);
  };

  const getItemPrice = (item: MarketItem) => (
    item.uoms?.find((u) => u.isOrderableUom)?.price
    ?? item.uoms?.find((u) => u.isPrimaryUom)?.price
  );

  const productColumns: TableColumn<MarketItem>[] = [
    {
      id: 'name',
      label: t('management.productName', 'Product'),
      render: (row) => {
        const name = row.marketItemName || row.itemName;
        return (
          <Tooltip title={name}
            placement="top">
            <span>{truncateText(name)}</span>
          </Tooltip>
        );
      },
    },
    { id: 'sku', label: t('management.productSku', 'SKU'), field: 'itemNumber' },
    {
      id: 'category',
      label: t('management.productCategory', 'Category'),
      render: (row) => row.marketPrimaryCategoryName || row.primaryCategoryName || '',
    },
    {
      id: 'price',
      label: t('management.productPrice', 'Price'),
      align: 'right',
      render: (row) => {
        const price = getItemPrice(row);
        return price !== undefined ? applyRoundAndFormat(price, selectedMarket?.currencyCode) : '';
      },
    },
  ];

  const handleViewStore = (store: MarketStore) => {
    if (!canEditStores) return;
    const params = new URLSearchParams();
    params.set('companyId', formData.companyId.toString());
    if (formData.customerTypeId) params.set('customerTypeId', formData.customerTypeId.toString());
    navigate(`/stores/edit/${store.customerId}?${params.toString()}`);
  };

  const storeColumns: TableColumn<MarketStore>[] = [
    {
      id: 'name',
      label: t('management.storeName', 'Store'),
      render: (row) => (
        <Tooltip title={row.storeName}
          placement="top">
          <span>{truncateText(row.storeName)}</span>
        </Tooltip>
      ),
    },
    { id: 'storeNumber', label: t('management.storeNumber', 'Store Number'), field: 'storeNumber' },
    { id: 'town', label: t('management.storeTown', 'Town'), field: 'town' },
    { id: 'country', label: t('management.storeCountry', 'Country'), field: 'country' },
  ];

  const overviewTabContent = (
    <Stack spacing={6}>
      <Box>
        <Grid
          container
          spacing={4}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <TextField
              fullWidth
              label={t('management.catalogueName')}
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
          >
            <TextField
              fullWidth
              label={t('management.erpInternalId')}
              value={formData.erpInternalId || ''}
              onChange={handleInputChange('erpInternalId')}
              type="number"
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
          >
            <Autocomplete
              options={instances}
              getOptionLabel={(option) => option.name}
              value={selectedInstance}
              onChange={handleInstanceChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('management.selectInstance')}
                  error={!!errors.instance}
                  helperText={errors.instance}
                  required
                />
              )}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
          >
            <Autocomplete
              options={markets}
              getOptionLabel={(option) => option.name}
              value={selectedMarket}
              onChange={handleMarketChange}
              disabled={!selectedInstance}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('management.selectMarket')}
                  error={!!errors.market}
                  helperText={errors.market}
                  required
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      {selectedMarket && (
        <Section style={{ mt: 0, p: 5, pb: 0, pt: 3 }}>
          <Box sx={{ px: { xs: 4, md: 5 }, pt: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 } }}>
            <Box sx={{ mb: 2, pb: 2 }}>
              <Typography
                variant="textMD"
                fontWeight={600}
              >
                {t('management.orderTypeAssignment')}
              </Typography>
            </Box>

            {errors.orderTypes && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
              >
                {errors.orderTypes}
              </Alert>
            )}

            <FormControl component="fieldset">
              <FormGroup>
                {orderTypes.map((orderType) => (
                  <FormControlLabel
                    key={orderType.orderTypeId}
                    control={
                      <Checkbox
                        checked={selectedOrderTypes.some(ot => ot.orderTypeId === orderType.orderTypeId)}
                        onChange={(e) => handleOrderTypeChange(orderType, e.target.checked)}
                      />
                    }
                    label={orderType.name}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Box>
        </Section>
      )}

      {isDsdSelected && (
        <Section style={{ mt: 0, p: 5, pb: 0, pt: 3 }}>
          <Box sx={{ px: { xs: 4, md: 5 }, pt: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 } }}>
            <Box sx={{ mb: 2, pb: 2 }}>
              <Typography
                variant="textMD"
                fontWeight={600}
              >
                {t('management.supplierAssignment')}
              </Typography>
            </Box>

            <Alert
              severity="info"
              sx={{ mb: 2 }}
            >
              {t('management.dsdOrderTypeNote')}
            </Alert>

            {errors.suppliers && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
              >
                {errors.suppliers}
              </Alert>
            )}

            <FormControl component="fieldset">
              <FormGroup>
                {suppliers.map((supplier) => (
                  <FormControlLabel
                    key={supplier.supplierId}
                    control={
                      <Checkbox
                        checked={selectedSuppliers.some(s => s.supplierId === supplier.supplierId)}
                        onChange={(e) => handleSupplierChange(supplier, e.target.checked)}
                      />
                    }
                    label={`${supplier.supplierNumber} - ${supplier.supplierName}`}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Box>
        </Section>
      )}
    </Stack>
  );

  const productsTabContent = !isEdit ? (
    <Section style={{ mt: 0, p: 5 }}>
      <Box sx={{ p: { xs: 4, md: 5 } }}>
        <Typography variant="textSM"
          color="text.secondary">
          {t('management.productsAvailableAfterCreate', 'Products assigned to this catalogue will show here once the catalogue has been created.')}
        </Typography>
      </Box>
    </Section>
  ) : !canViewCatalogueProducts ? (
    <Section style={{ mt: 0, p: 5 }}>
      <Box sx={{ p: { xs: 4, md: 5 } }}>
        <Typography variant="textSM"
          color="text.secondary">
          {t('management.productsPermissionRequired', 'You do not have permission to view products.')}
        </Typography>
      </Box>
    </Section>
  ) : (
    <TableSection<MarketItem>
      title={t('management.products', 'Products')}
      columns={productColumns}
      rows={pagedCatalogueItems}
      getRowId={(row) => row.contentId ?? row.itemNumber}
      onRowClick={canEditProducts ? handleViewProduct : undefined}
      recordCount={filteredCatalogueItems.length}
      recordCountLabel={t('management.products', 'Products')}
      loading={isItemsLoading}
      searchValue={productsSearchInput}
      onSearchChange={setProductsSearchInput}
      searchPlaceholder={t('management.searchProductsPlaceholder', 'Search products...')}
      page={productsPage}
      maxPages={productsTotalPages}
      onPageChange={setProductsPage}
    />
  );

  const storesTabContent = !isEdit ? (
    <Section style={{ mt: 0, p: 5 }}>
      <Box sx={{ p: { xs: 4, md: 5 } }}>
        <Typography variant="textSM"
          color="text.secondary">
          {t('management.storesAvailableAfterCreate', 'Stores assigned to this catalogue will show here once the catalogue has been created.')}
        </Typography>
      </Box>
    </Section>
  ) : !canViewCatalogueStores ? (
    <Section style={{ mt: 0, p: 5 }}>
      <Box sx={{ p: { xs: 4, md: 5 } }}>
        <Typography variant="textSM"
          color="text.secondary">
          {t('management.storesPermissionRequired', 'You do not have permission to view stores.')}
        </Typography>
      </Box>
    </Section>
  ) : (
    <TableSection<MarketStore>
      title={t('management.stores', 'Stores')}
      columns={storeColumns}
      rows={assignedStores}
      getRowId={(row) => row.customerId}
      onRowClick={canEditStores ? handleViewStore : undefined}
      recordCount={assignedStoresTotalCount}
      recordCountLabel={t('management.stores', 'Stores')}
      loading={isStoresLoading}
      searchValue={storesSearchInput}
      onSearchChange={setStoresSearchInput}
      searchPlaceholder={t('management.searchStoresPlaceholder', 'Search stores...')}
      page={storesPage}
      maxPages={storesTotalPages}
      onPageChange={setStoresPage}
    />
  );

  const tabs: TabDefinition[] = [
    {
      id: 'overview',
      label: t('management.overview', 'Overview'),
      content: overviewTabContent,
    },
    {
      id: 'products',
      label: t('management.products', 'Products'),
      content: productsTabContent,
    },
    {
      id: 'stores',
      label: t('management.stores', 'Stores'),
      content: storesTabContent,
    },
  ];

  const dsdOrderTypeCount = selectedOrderTypes.filter(ot => isOrderTypeDSD(ot.orderTypeId)).length;
  const isErpMapped = formData.erpInternalId !== undefined && formData.erpInternalId !== null;

  return (
    <DetailPageTemplate
      title={isEdit ? t('management.editCatalogue') : t('management.createCatalogue')}
      subtitle={t('management.catalogueFormSubtitle', 'Manage catalogue setup, order channels, and supplier assignments.')}
      loading={isLoading}
      loadingLabel={t('management.loading')}
      actions={(
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={handleCancel}
            disabled={isSaving}
          >
            {t('management.cancel')}
          </Button>
          {userCanSave && (
            <Button
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? t('management.saving') : (isEdit ? t('management.update') : t('management.create'))}
            </Button>
          )}
        </>
      )}
      headlineContent={{
        layout: 'six',
        insights: [
          <InsightTile
            key="products"
            label={t('management.tileProductsLabel', 'Products')}
            value={canViewCatalogueProducts ? `${activeCatalogueItemsCount} / ${catalogueItems.length}` : '—'}
            description={t('management.tileProductsDescription', 'Active of assigned products')}
            icon={<Inventory2 />}
            variant="primary"
            loading={isEdit && canViewCatalogueProducts && isItemsLoading}
          />,
          <InsightTile
            key="categories"
            label={t('management.tileCategoriesLabel', 'Categories')}
            value={canViewCatalogueProducts ? catalogueCategoriesCount : '—'}
            description={t('management.tileCategoriesDescription', 'Distinct categories in this catalogue')}
            icon={<CategoryIcon />}
            variant="secondary"
            loading={isEdit && canViewCatalogueProducts && isItemsLoading}
          />,
          <InsightTile
            key="stores"
            label={t('management.tileStoresLabel', 'Stores')}
            value={canViewCatalogueStores ? assignedStoresTotalCount : '—'}
            description={t('management.tileStoresDescription', 'Stores this catalogue is assigned to')}
            icon={<Storefront />}
            variant="success"
            loading={isEdit && canViewCatalogueStores && isStoresLoading}
          />,
          <InsightTile
            key="orderTypes"
            label={t('management.tileOrderTypesLabel', 'Order Channels')}
            value={selectedOrderTypes.length}
            description={t('management.tileOrderTypesDescription', 'Order channels assigned to this catalogue')}
            icon={<Rule />}
            variant="primary"
          />,
          <InsightTile
            key="suppliers"
            label={t('management.tileSuppliersLabel', 'Suppliers')}
            value={selectedSuppliers.length}
            description={dsdOrderTypeCount > 0
              ? t('management.tileSuppliersDescription', 'Suppliers assigned for DSD ordering')
              : t('management.tileSuppliersNoDsdDescription', 'No DSD order type assigned')}
            icon={<LocalShipping />}
            variant="warning"
          />,
          <InsightTile
            key="erp"
            label={t('management.tileErpLabel', 'ERP Mapping')}
            value={isErpMapped ? t('management.tileErpMapped', 'Mapped') : t('management.tileErpNotMapped', 'Not Mapped')}
            description={isErpMapped
              ? `${t('management.erpInternalId')}: ${formData.erpInternalId}`
              : t('management.tileErpNoIdDescription', 'No ERP integration configured')}
            icon={<Sync />}
            variant={isErpMapped ? 'success' : 'default'}
          />,
        ],
      }}
      tabs={tabs}
      tabsAriaLabel={t('management.catalogueName')}
    >
      <MessagesSnackbar
        open={showMessage}
        message={message}
        severity={isSuccess ? 'success' : 'error'}
        duration={5000}
        onClose={handleCloseMessage}
      />
    </DetailPageTemplate>
  );
};

export default CatalogueForm;
